import { SEARCH_PLANS } from 'pages/dashboard/dashboard.api';
import { chooseMovingPlanTemplate } from 'pages/settings/settings.utils';
import { VNM_STANDARD_PLAN_TEMPLATE_ID } from 'domain/plans';
import { trackError } from 'domain/tracking';
import { GET_ACCOUNT_PLANS_COUNT } from 'hooks/use-account-plans-count.hook';
import { GET_PLANS_FOR_ADD_MODAL } from 'pages/settings/secondary/payment-methods/payment-methods.api';
import { GET_ACCOUNT_SETTINGS_INFO } from 'pages/settings/components/account-settings/account-settings.api';
import {
  GET_ACCOUNT_INFO_QUERY,
  CREATE_MOVE_REQUEST,
  UPDATE_MOVE_REQUEST,
  GET_PLAN_TEMPLATE_INFO_QUERY,
  CREATE_PLAN,
  CREATE_SETUP_INTENT,
  GET_MOVERS_PAYMENT_METHODS,
  FETCH_BILLING_EXPERIENCE_OPTIONS,
} from './services.api';

export class ApolloServiceHandler {
  apolloClient: any;

  userId: any;

  accountId: number;

  constructor(apolloClient, userId, accountId) {
    this.apolloClient = apolloClient;
    this.userId = userId;
    this.accountId = accountId;
    this.getAccountInfo.bind(this);
    this.createOrUpdateMoveRequest.bind(this);
    this.createPlan.bind(this);
    this.getNewPlanAmountCents.bind(this);
    this.createSetupIntent.bind(this);
    this.fetchExistingPaymentMethodIds.bind(this);
    this.pollForPaymentCreation.bind(this);
  }

  getAccountInfo = async (context: MovingStateMachineContext) => {
    const { data } = await this.apolloClient.query({
      query: GET_ACCOUNT_INFO_QUERY,
      // Always fetch the selected account to ensure the most
      // recent move request is in the cache
      fetchPolicy: 'network-only',
      variables: {
        id: context.accountId,
      },
    });
    return { account: data.account };
  };

  createMoveRequest = async (context: MovingStateMachineContext) => {
    const { data } = await this.apolloClient.mutate({
      mutation: CREATE_MOVE_REQUEST,
      variables: {
        input: {
          oldUtilityAccountId: context.selectedUtilityAccount?.id,
          serviceAddressZip: context.zipCode,
          utilityId: context.utility.id,
          newPlanNickname: context.nickname,
        },
      },
    });
    return { moveRequest: data.createMoveRequest };
  };

  updateMoveRequest = async (context: MovingStateMachineContext) => {
    const { data } = await this.apolloClient.mutate({
      mutation: UPDATE_MOVE_REQUEST,
      variables: {
        input: {
          id: context.moveRequest.id,
          serviceAddressZip: context.zipCode,
          utilityId: context.utility.id,
          newPlanNickname: context.nickname,
          newUtilityAccountId: context.newUtilityAccountId,
          status: context.status,
        },
      },
    });
    return { moveRequest: data.updateMoveRequest };
  };

  createOrUpdateMoveRequest = (context: MovingStateMachineContext) => {
    const mutation = context.moveRequest
      ? this.updateMoveRequest
      : this.createMoveRequest;
    return mutation(context);
  };

  getNewPlanAmountCents = async (context: MovingStateMachineContext) => {
    const { data } = await this.apolloClient.query({
      query: GET_PLAN_TEMPLATE_INFO_QUERY,
      variables: { visibility: context.utility.vnmEligible ? 'vnm' : 'public' },
    });
    const planTemplate = chooseMovingPlanTemplate({
      availablePlanTemplates: data?.planTemplates,
      oldPlanTemplateId: context.selectedPlan?.planTemplateId,
    });
    return {
      newPlanAmountCents:
        planTemplate?.products.find(
          product => product.productType === 'Membership'
          // If a product doesn't have a default amount, the amount will be 0.
          // If we wanted to create a new pricing schema, we'd create a new product
        )?.defaultAmountCents || 0,
    };
  };

  createPlan = async (context: MovingStateMachineContext) => {
    const vnmEligible = context.utility.vnmEligible;
    const shouldCreatePlanBasedOnBillingExperience =
      context.utility.shouldCreatePlanBasedOnBillingExperience;
    let billingExperienceOption: string | undefined;
    let paymentMethodId: number | null | undefined;
    let planTemplateId: number | undefined;

    // The shouldCreatePlanBasedOnBillingExperience is controlled from the backend and is used to determine
    // if we should create a plan based on billing experience or not. As of today,
    // this will be true only for NJ utilities.
    if (shouldCreatePlanBasedOnBillingExperience) {
      const billingExperienceOptions = await this.fetchBillingExperienceOptions(
        context
      );
      billingExperienceOption =
        billingExperienceOptions?.billingExperienceOptions?.[0]?.name;
    } else {
      const { planTemplateData } = await this.getPlanTemplateData(context);
      const planTemplate = chooseMovingPlanTemplate({
        availablePlanTemplates: planTemplateData.planTemplates,
        oldPlanTemplateId: context.selectedPlan?.planTemplateId,
      });
      // not all vnm eligible utilities have a vnm plan template
      planTemplateId = vnmEligible
        ? planTemplate?.id || VNM_STANDARD_PLAN_TEMPLATE_ID
        : planTemplate?.id;

      if (context.paymentMethodId !== undefined)
        paymentMethodId = context.paymentMethodId;
      else if (planTemplateData.planTemplates.length > 0)
        paymentMethodId = planTemplate?.requiresPaymentMethod
          ? context.selectedPlan?.paymentMethodId
          : undefined;
    }

    const searchPlans = (status: string) => ({
      query: SEARCH_PLANS,
      variables: { accountId: this.accountId, page: 1, statuses: [status] },
    });
    const accountSettingsQuery = {
      query: GET_ACCOUNT_PLANS_COUNT,
      variables: { accountId: this.accountId },
    };
    const addPaymentModalQuery = {
      query: GET_PLANS_FOR_ADD_MODAL,
      variables: { accountId: this.accountId },
    };

    const refetchQueries = [
      searchPlans('active'),
      searchPlans('inactive'),
      accountSettingsQuery,
      addPaymentModalQuery,
    ];

    const baseMutationInput = {
      accountId: context.selectedUtilityAccount?.accountId,
      planName: context.nickname,
      paymentMethodId,
      customerClass: context.selectedUtilityAccount?.customerClass,
      utilityId: context.utility?.id,
    };

    // Only include planTemplateId if billingExperience is not present
    const mutationInput = billingExperienceOption
      ? { ...baseMutationInput, billingExperience: billingExperienceOption }
      : { ...baseMutationInput, planTemplateId };

    const { data: createPlanData } = await this.apolloClient.mutate({
      mutation: CREATE_PLAN,
      awaitRefetchQueries: true,
      refetchQueries,
      variables: {
        input: mutationInput,
      },
    });
    return {
      newPlanId: createPlanData.createPlanWithUtilityAccount.id,
      newUtilityAccountId:
        createPlanData.createPlanWithUtilityAccount.utilityAccountId,
      status: 'fulfilled',
    };
  };

  getPlanTemplateData = async (context: MovingStateMachineContext) => {
    const vnmEligible = context.utility.vnmEligible;
    const { data: planTemplateData } = await this.apolloClient.query({
      query: GET_PLAN_TEMPLATE_INFO_QUERY,
      variables: {
        visibility: vnmEligible ? 'vnm' : 'public',
        utilityId: vnmEligible ? context.utility.id : undefined,
      },
    });
    return { planTemplateData };
  };

  createSetupIntent = async () => {
    const { data } = await this.apolloClient.mutate({
      mutation: CREATE_SETUP_INTENT,
      fetchPolicy: 'no-cache',
      variables: {
        accountId: this.accountId,
      },
    });

    return { stripeClientSecret: data.createSetupIntent.stripeClientSecret };
  };

  fetchExistingPaymentMethodIds = async () => {
    const { data } = await this.apolloClient.query({
      query: GET_MOVERS_PAYMENT_METHODS,
      variables: { accountId: this.accountId },
    });
    return {
      existingPaymentMethodIds: data.account.paymentMethods.map(
        method => method.id
      ),
    };
  };

  pollForPaymentCreation = async (context: MovingStateMachineContext) => {
    const variables = { accountId: this.accountId };
    const observer = this.apolloClient.watchQuery({
      query: GET_MOVERS_PAYMENT_METHODS,
      variables,
      pollInterval: 1000,
    });
    let foundPaymentMethod;

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        observer.stopPolling();
        if (!foundPaymentMethod) {
          const error = new Error('Polling for payment method timed out');
          trackError({ error });
          return reject(error);
        }
      }, 20000);

      observer.subscribe({
        next: ({ data }) => {
          const newPaymentMethod = data.account.paymentMethods.find(
            method => !context.existingPaymentMethodIds?.includes(method.id)
          );
          if (newPaymentMethod) {
            observer.stopPolling();
            foundPaymentMethod = true;
            resolve({ paymentMethodId: newPaymentMethod.id });
          }
        },
        error: e => reject(new Error(e)),
      });
    });
  };

  fetchUtilityAccountsBillingExperience = async (
    context: MovingStateMachineContext
  ) => {
    const selectedAccountId = context.accountId;
    const { data } = await this.apolloClient.query({
      query: GET_ACCOUNT_SETTINGS_INFO,
      variables: { accountId: selectedAccountId },
      skip: !selectedAccountId,
    });
    const { account } = data || {};
    const utilityAccounts = account?.utilityAccounts || [];
    const utilityAccountBillingExperience = utilityAccounts.map(
      ({ id, activeBillingSettings, funnelBillExperience }) => {
        const billExperience =
          activeBillingSettings?.billExperience ?? funnelBillExperience;
        return { id, billExperience };
      }
    );
    return { utilityAccountBillingExperience };
  };

  fetchBillingExperienceOptions = async (
    context: MovingStateMachineContext
  ) => {
    const enrollmentDataSourceName =
      context.enrollmentOptions?.length === 1
        ? context.enrollmentOptions[0]
        : 'utility_connect';
    const { data } = await this.apolloClient.query({
      query: FETCH_BILLING_EXPERIENCE_OPTIONS,
      variables: {
        utilityId: context.utility.id,
        enrollmentDataSourceName,
      },
    });
    return data;
  };
}
